import type { SeoMeta } from '@dma/types';
import { InvalidServiceError } from '../errors/InvalidServiceError';

const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const TITLE_MIN = 2;
const TITLE_MAX = 100;
const SUMMARY_MIN = 10;
const SUMMARY_MAX = 200;

export interface ServiceInput {
  id: string;
  title: string;
  slug: string;
  summary: string;
  description: string;
  icon: string;
  image: string;
  seo: SeoMeta;
}

/**
 * Entity per the pattern in features/contact/domain/entities/ContactMessage.entity.ts.
 * Has identity (`id`) but no lifecycle transitions (unlike ContactMessage's
 * new/read/archived) — a Service is either published or it doesn't exist
 * in the content source at all.
 */
export class Service {
  private constructor(public readonly props: ServiceInput) {
    Object.freeze(this.props);
    Object.freeze(this);
  }

  public static create(input: ServiceInput): Service {
    const title = input.title.trim();
    const slug = input.slug.trim();
    const summary = input.summary.trim();
    const description = input.description.trim();

    if (title.length < TITLE_MIN || title.length > TITLE_MAX) {
      throw new InvalidServiceError(`title must be between ${TITLE_MIN} and ${TITLE_MAX} characters.`);
    }
    if (!SLUG_PATTERN.test(slug)) {
      throw new InvalidServiceError(`slug "${slug}" must be lowercase kebab-case.`);
    }
    if (summary.length < SUMMARY_MIN || summary.length > SUMMARY_MAX) {
      throw new InvalidServiceError(
        `summary must be between ${SUMMARY_MIN} and ${SUMMARY_MAX} characters.`
      );
    }
    if (description.length === 0) {
      throw new InvalidServiceError('description must not be empty.');
    }
    if (input.icon.trim().length === 0) {
      throw new InvalidServiceError('icon must not be empty.');
    }
    if (input.image.trim().length === 0) {
      throw new InvalidServiceError('image must not be empty.');
    }

    return new Service({ ...input, title, slug, summary, description });
  }
}
